import {useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {useBoolean} from 'shared/web';

import {validateVin} from 'features/vin-validate';

const VIN_LENGTH = 17;

const getVinError = (value: string | null): string | null => {
  const trimmed = value?.trim() ?? '';

  if (isNilOrEmpty(trimmed)) {
    return i18n.t('entity.vehicle.notifications.vinIsRequired');
  }

  if (!validateVin(trimmed)) {
    return i18n.t('entity.vehicle.notifications.vinIsInvalid');
  }

  return null;
};

const getMileageError = (value: number | null): string | null => {
  if (isNil(value)) {
    return i18n.t('general.validations.fieldIsRequired');
  }

  if (value <= 0) {
    return i18n.t('general.notifications.mileagePositive');
  }

  return null;
};

interface UseCarPassFormArgs {
  hasMissingVin: boolean;
  hasMissingMileage: boolean;
}

export function useCarPassForm(args: UseCarPassFormArgs) {
  const [isValidating, enableValidation] = useBoolean(false);
  const [vinInput, setVinInput] = useState<string | null>(null);
  const [mileageInput, setMileageInput] = useState<number | null>(null);

  const vinError = isValidating && args.hasMissingVin ? getVinError(vinInput) : null;
  const mileageError =
    isValidating && args.hasMissingMileage ? getMileageError(mileageInput) : null;

  const validate = () => {
    enableValidation();

    const currentVinError = args.hasMissingVin ? getVinError(vinInput) : null;
    const currentMileageError = args.hasMissingMileage ? getMileageError(mileageInput) : null;

    return {
      isValid: isNil(currentVinError) && isNil(currentMileageError),
    };
  };

  return {
    vinInput,
    setVinInput,
    mileageInput,
    setMileageInput,
    vinError,
    mileageError,
    vinLength: VIN_LENGTH,
    validate,
  };
}
